// ── Consultation appointments (Admin → Companies, workspace area) ──────────────────────────────
// Which connected mailbox the consultation invoices are read from, and which of the Calendar page's
// calendars their appointments are written to. Server single point: services/consultCalendar.ts.
// "Check now" reads the mailbox and reports what it found without writing anything or emailing anybody.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { consultcalApi, ConsultCalendarView, ConsultCalendarSave, ConsultCalendarTest } from '../../utils/api/consultcal';
import { fmtDate, fmtDateTime } from '../../utils/region';

const input: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none',
};
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 20, marginBottom: 16 };
const hint: React.CSSProperties = { fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 };
const warn: React.CSSProperties = { fontSize: 11, color: '#B42318', marginTop: 4 };
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color, fontSize: 13, fontWeight: 600, cursor: 'pointer' });
const ghost: React.CSSProperties = { padding: '6px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' };

export default function ConsultCalendarSettings() {
  const { t } = useTranslation();
  const [v, setV] = useState<ConsultCalendarView | null>(null);
  const [mailbox, setMailbox] = useState('');
  const [calendar, setCalendar] = useState('');
  const [test, setTest] = useState<ConsultCalendarTest | null>(null);
  const [err, setErr] = useState(''); const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false); const [testing, setTesting] = useState(false);

  const load = (d: ConsultCalendarView) => { setV(d); setMailbox(d.mailboxEmail || ''); setCalendar(d.calendarId || ''); };
  useEffect(() => {
    consultcalApi.get().then(d => { setV(d); setMailbox(d.mailboxEmail || ''); setCalendar(d.calendarId || ''); }).catch((e: any) => setErr(e.message));
  }, []);

  const save = async () => {
    setSaving(true); setErr(''); setMsg('');
    const body: ConsultCalendarSave = { mailboxEmail: mailbox, calendarId: calendar };
    try {
      load(await consultcalApi.save(body));
      setMsg(t('ccs_saved')); setTimeout(() => setMsg(''), 2000);
    } catch (e: any) { setErr(e.message); } finally { setSaving(false); }
  };
  const checkNow = async () => {
    setTesting(true); setErr(''); setTest(null);
    try { setTest(await consultcalApi.test()); }
    catch (e: any) { setErr(e.message); } finally { setTesting(false); }
  };

  if (!v) {
    return (
      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('ccs_title')}</div>
        {err && <div style={warn}>{err}</div>}
      </div>
    );
  }

  const eff = v.effective;
  const run = v.lastRun;
  const knownMailbox = v.accounts.some(a => a.email === mailbox);
  return (
    <div style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t('ccs_title')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 14 }}>{t('ccs_hint')}</div>
      {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 12 }}>{err}</div>}

      {/* The mailbox the invoices are read from — one of the connected accounts */}
      <div style={{ marginBottom: 14 }}>
        <label style={lbl}>{t('ccs_mailbox')}</label>
        <select value={mailbox} onChange={e => setMailbox(e.target.value)} style={input}>
          <option value="">{t('ccs_mailbox_none')}</option>
          {mailbox && !knownMailbox && <option value={mailbox}>{mailbox} ({t('ccs_not_connected')})</option>}
          {v.accounts.map(a => <option key={a.email} value={a.email}>{a.email}{a.name && a.name.toLowerCase() !== a.email ? ` — ${a.name}` : ''}</option>)}
        </select>
        <div style={hint}>{t('ccs_mailbox_hint')}</div>
      </div>

      {/* The calendar the appointments are written to — one of the Calendar page's accounts */}
      <div style={{ marginBottom: 14 }}>
        <label style={lbl}>{t('ccs_calendar')}</label>
        <select value={calendar} onChange={e => setCalendar(e.target.value)} style={input}>
          <option value="">{t('ccs_calendar_none')}</option>
          {v.calendars.map(c => <option key={c.id} value={c.id}>{c.label} — {c.email}</option>)}
        </select>
        <div style={hint}>{t('ccs_calendar_hint')}</div>
        {eff.ready
          ? <div style={{ ...hint, color: 'var(--text-secondary)' }}><i className="ti ti-circle-check" style={{ marginRight: 4, color: '#1D9E75' }} />{t('ccs_ready', { mailbox: eff.mailbox, calendar: eff.calendar })}</div>
          : <div style={warn}><i className="ti ti-alert-triangle" style={{ marginRight: 4 }} />{eff.error}</div>}
      </div>

      {/* What the last visit to the mailbox did */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          <i className="ti ti-calendar-plus" style={{ marginRight: 4 }} />
          {run.at
            ? t('ccs_last_run', { when: fmtDateTime(run.at), seen: run.candidates, made: run.created, moved: run.moved })
            : t('ccs_last_run_never')}
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={checkNow} disabled={testing || !eff.ready} style={ghost}>{testing ? t('ccs_checking') : t('ccs_check')}</button>
      </div>
      {!!run.errors.length && <div style={warn}>{run.errors.join(' · ')}</div>}

      {/* "Check now" — read-only: what the scanner sees in the mailbox right now */}
      {test && (
        <div style={{ marginTop: 10, background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: test.appointments.length ? 6 : 0 }}>
            {t('ccs_test_summary', { scanned: test.emailsScanned, found: test.candidates, stale: test.stale })}
          </div>
          {test.appointments.map(a => (
            <div key={`${a.invoiceNo}-${a.date}-${a.time}`} style={{ fontSize: 12, color: 'var(--text-primary)' }}>
              <b>{a.invoiceNo}</b> · {fmtDate(a.date)} {a.time} · {t('ccs_minutes', { n: a.minutes })} · {a.client}
            </div>
          ))}
          {!!test.errors.length && <div style={warn}>{test.errors.join(' · ')}</div>}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
        {msg && <span style={{ fontSize: 12, color: '#1D9E75' }}><i className="ti ti-check" style={{ marginRight: 4 }} />{msg}</span>}
        <button onClick={save} disabled={saving} style={btn('var(--accent)')}>{saving ? t('ccs_saving') : t('ccs_save')}</button>
      </div>
    </div>
  );
}
